import BaseWidget from './baseWidget.js';
import { pie, euro } from './chartHelpers.js';
import stammdatenService from '../../services/stammdatenService.js';
import dayjs from '../../../../../shared/services/dayjs.js';
import { getAktuellerZeitraum } from '../../util.js';

/**
 * Zeigt die Verteilung der monatlichen AG-Lohnkosten als Kreisdiagramm,
 * aufgeschlüsselt nach Bereich. Datenquelle ist die Bereichsauswertungs-
 * Pipeline (BerichtService._runBereichsauswertungBetriebPipeline), also
 * dieselbe Berechnung wie in der bestehenden Bereichsauswertung.
 *
 * Der Zeitraum lässt sich über einen widget-eigenen Monatswähler im Body
 * unabhängig vom globalen Monatswähler einstellen und wird im Widget-State
 * persistiert.
 */
export default class LohnkostenBereichWidget extends BaseWidget {
	getDefaultRowSpan() { return 1; }

	constructor(type, instanceId, state = {}) {
		super(type, instanceId, state);
		this.zeitraum = getAktuellerZeitraum();
	}

	serializeState() {
		return { zeitraum: this.zeitraum, rowSpan: this.state?.rowSpan };
	}

	async onRender() {
		this.bodyEl.innerHTML = `
			<div class="widget-lohnkosten-wrap">
				<div class="widget-lohnkosten-toolbar">
					<div class="widget-lohnkosten-total">
						<span class="amount"></span>
						<span class="sub">AG-Lohnkosten / Monat</span>
					</div>
					<label class="widget-lohnkosten-month" for="lohnkosten-bereich-monat" onmousedown='event.preventDefault(); this.querySelector("input").showPicker()'>
						<button class="widget-lohnkosten-nav-btn" type="button" aria-label="Vorheriger Monat" data-step="-1" onmousedown="event.stopPropagation()"><i class="bi bi-chevron-left"></i></button>
						<i class="bi bi-calendar3"></i>
						<input type="month" name="lohnkosten-bereich-monat" aria-label="Lohnkosten Zeitraum" />
						<button class="widget-lohnkosten-nav-btn" type="button" aria-label="Nächster Monat" data-step="1" onmousedown="event.stopPropagation()"><i class="bi bi-chevron-right"></i></button>
					</label>
				</div>
				<div class="widget-lohnkosten-chart"></div>
				<span id="widget-lohnkosten-bereich-legend" style="font-size: 10px;color: var(--muted);">* Berechnete AG-Kosten</span>
			</div>
		`;
		const input = this.bodyEl.querySelector('input[type="month"]');
		input.value = dayjs(this.zeitraum).format('YYYY-MM');
		const stepMonth = (delta) => {
			this.zeitraum = dayjs(this.zeitraum).add(delta, 'month').format('YYYY-MM-DD');
			input.value = dayjs(this.zeitraum).format('YYYY-MM');
			this.renderChart();
		};
		input.addEventListener('change', () => {
			if (!input.value) return;
			this.zeitraum = dayjs(input.value).format('YYYY-MM-DD');
			this.renderChart();
		});
		this.bodyEl.querySelectorAll('.widget-lohnkosten-nav-btn').forEach((btn) => {
			btn.addEventListener('click', () => stepMonth(Number(btn.dataset.step)));
		});
		await this.renderChart();
	}

	async renderChart() {
		const chartEl = this.bodyEl.querySelector('.widget-lohnkosten-chart');
		const totalEl = this.bodyEl.querySelector('.widget-lohnkosten-total .amount');
		if (!chartEl) return;
		chartEl.innerHTML = '<div class="widget-empty">Lade...</div>';
		if (totalEl) totalEl.textContent = '0,00 €';
		const betriebID = stammdatenService.aktuelleBetriebsstaette;
		if (!betriebID) {
			chartEl.innerHTML = '<div class="widget-empty">Keine Betriebsstätte ausgewählt.</div>';
			return;
		}
		try {
			const params = new URLSearchParams({ zeitraum: this.zeitraum });
			const url = `/neolohn/api/dashboard/lohnkosten-bereich/${betriebID}?${params.toString()}`;
			const response = await fetch(url);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			const rows = await response.json();
			if (!Array.isArray(rows) || rows.length === 0) {
				chartEl.innerHTML = '<div class="widget-empty">Keine Lohnkostendaten für diesen Zeitraum.</div>';
				return;
			}
			const data = rows.map((r) => ({ label: r.Bereich, value: r.AgKosten }));
			const total = data.reduce((s, d) => s + d.value, 0);
			if (totalEl) totalEl.textContent = euro(total);
			chartEl.innerHTML = pie(data, {
				format: euro,
				showSummary: false,
			});
		} catch (err) {
			console.error('LohnkostenBereichWidget render failed:', err);
			chartEl.innerHTML = '<div class="widget-empty">Fehler beim Laden der Lohnkosten.</div>';
		}
	}
}
